import { FC, Fragment, MouseEvent, useState } from 'react';
import { Box, ButtonBase, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { ChanModel } from '../../services';
import styles from './JourneyChatPanel.module.scss';

interface JourneyModelPickerProps {
  models: ChanModel[];
  // '' is Auto: each step gets the right model from the business's provider.
  value: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  // Create image lists image models under its own heading, and Auto means something else there.
  heading?: string;
  autoDescription?: string;
}

const AUTO = { id: '', label: 'Auto', description: 'Picks the right model for each step' };

// The models the business's own keys can use, grouped by provider, each family following its newest
// version. A picked model runs the whole task.
const JourneyModelPicker: FC<JourneyModelPickerProps> = ({
  models,
  value,
  onChange,
  disabled,
  heading = 'Model',
  autoDescription = AUTO.description,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = models.find((model) => model.id === value);
  const triggerLabel = selected?.label ?? AUTO.label;
  const providers = Array.from(new Set(models.map((model) => model.providerLabel)));

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (modelId: string) => {
    onChange(modelId);
    handleClose();
  };

  if (!models.length) return null;

  const option = (id: string, label: string, description: string) => {
    const isSelected = id === (selected?.id ?? AUTO.id);
    return (
      <MenuItem
        key={id || 'auto'}
        role="option"
        aria-selected={isSelected}
        selected={isSelected}
        onClick={() => handleSelect(id)}
        className={styles.modelOption}
      >
        <Box className={styles.modelOptionText}>
          <Box className={styles.modelOptionTitleRow}>
            <span className={styles.modelOptionTitle}>{label}</span>
          </Box>
          <span className={styles.modelOptionDescription}>{description}</span>
        </Box>
        <CheckIcon className={styles.modelOptionCheck} style={{ visibility: isSelected ? 'visible' : 'hidden' }} />
      </MenuItem>
    );
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        disabled={disabled}
        className={styles.modelTrigger}
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchorEl)}
        aria-label={`${heading}: ${triggerLabel}. Change model`}
      >
        <span className={styles.modelTriggerLabel}>{triggerLabel}</span>
        <ExpandLessIcon className={styles.modelTriggerIcon} />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { className: styles.modelMenu } }}
        // The theme caps every Menu list at 250px; this one should show all models at once.
        MenuListProps={{ role: 'listbox', dense: true, sx: { maxHeight: 'none' } }}
      >
        <Typography className={styles.modelMenuHeading}>{heading}</Typography>
        {option(AUTO.id, AUTO.label, autoDescription)}
        {providers.map((provider) => (
          <Fragment key={provider}>
            <Typography className={styles.modelMenuHeading}>{provider}</Typography>
            {models
              .filter((model) => model.providerLabel === provider)
              .map((model) => option(model.id, model.label, model.description))}
          </Fragment>
        ))}
      </Menu>
    </>
  );
};

export default JourneyModelPicker;
